import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { usePlaidLink } from 'react-plaid-link';
import { Link as LinkIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';

/** Plaid Link: asks the server for a link token, opens Plaid, exchanges the result. */
export default function ConnectBankButton({ onConnected, label = 'Connect bank account', className }: {
  onConnected: () => void; label?: string; className?: string;
}) {
  const [linkToken, setLinkToken] = useState<string | null>(null);

  const createToken = useMutation(
    () => api.post('/banking/link-token').then(r => r.data.link_token),
    {
      onSuccess: (token: string) => setLinkToken(token),
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not start bank connection'); },
    }
  );

  const exchange = useMutation(
    ({ publicToken, institutionName }: { publicToken: string; institutionName: string }) =>
      api.post('/banking/exchange-token', { public_token: publicToken, institution_name: institutionName }),
    {
      onSuccess: (r: any) => { toast.success(`${r.data.institution} connected!`); onConnected(); },
      onError: () => { toast.error('Could not connect bank'); },
    }
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken, metadata) => {
      exchange.mutate({ publicToken, institutionName: metadata.institution?.name || 'Bank' });
    },
    onExit: () => setLinkToken(null),
  });

  const handleClick = () => {
    if (linkToken && ready) { open(); return; }
    createToken.mutate();
  };

  React.useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  return (
    <button
      onClick={handleClick}
      disabled={createToken.isLoading || exchange.isLoading}
      className={className || 'btn-primary text-sm'}
    >
      <LinkIcon className="h-4 w-4" />
      {createToken.isLoading ? 'Connecting…' : label}
    </button>
  );
}
